import type { LocalDatabase } from '../core/types';
import type { QueryBuilderSpec } from '~/powersync/hooks/useLivePowerSyncQuery';

export const ChatQueries = {
  /**
   * Find all messages for a specific chat
   */
  findMessages: (chatId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('ChatMessage')
        .selectAll()
        .where('chatId', '=', chatId)
        .orderBy('createdAt', 'asc'),
    queryKey: ['chat', 'messages', chatId],
  }),

  /**
   * Find all parts for a specific message
   */
  findMessageParts: (messageId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('ChatMessagePart')
        .selectAll()
        .where('messageId', '=', messageId)
        .orderBy('order', 'asc'),
    queryKey: ['chat', 'message', messageId, 'parts'],
  }),

  /**
   * Find a single chat by ID
   */
  findById: (id: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .selectAll()
        .where('id', '=', id)
        .where('deletedAt', 'is', null),
    queryKey: ['chat', 'id', id],
  }),

  /**
   * Find a single chat by its `niceId` (the shareable id used in the URL).
   * Single-record; `createQuery` is null while `niceId` is falsy (a chat carries
   * a null niceId until the backend backfills it and PowerSync syncs it back).
   * Not filtered by userId for the same reason as the other chat queries
   * (locally-created rows have a null userId until sync).
   */
  findByNiceId: (
    niceId: string | null | undefined,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: niceId
      ? (db: LocalDatabase) =>
          db
            .selectFrom('Chat')
            .selectAll()
            .where('niceId', '=', niceId)
            .where('deletedAt', 'is', null)
            .limit(1)
      : null,
    queryKey: ['chat', 'niceId', niceId ?? 'null'],
  }),

  /**
   * Find the most-recently-created non-deleted chat for this device. Not
   * filtered by userId (locally-created rows have a null userId until sync), so
   * an offline just-created chat still resolves. Used by the bare `/chat`
   * landing route to pick which conversation to open, mirroring the row
   * `getOrCreateChat` would select.
   */
  findMostRecentActive: (): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .selectAll()
        .where('deletedAt', 'is', null)
        .orderBy('createdAt', 'desc')
        .limit(1),
    queryKey: ['chat', 'mostRecentActive'],
  }),

  /**
   * Find the most recent text part across a chat's messages — used for the
   * one-line preview in the chat-list sidebar.
   */
  findLastTextPart: (chatId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('ChatMessagePart')
        .selectAll()
        .where('chatId', '=', chatId)
        .where('type', '=', 'text')
        .orderBy('createdAt', 'desc')
        .limit(1),
    queryKey: ['chat', 'lastTextPart', chatId],
  }),

  /**
   * Find the most recent non-deleted chat scoped to a routing context and its
   * reference (e.g. WORKOUT + workout id, or MEAL + "yyyy-MM-dd:MEALTYPE"). Used
   * by the right-rail chat so each workout / meal-day gets its own conversation,
   * independent of the global GENERAL chat.
   */
  findByContext: (
    context: string,
    contextRefId: string,
  ): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .selectAll()
        .where('context', '=', context)
        .where('contextRefId', '=', contextRefId)
        .where('deletedAt', 'is', null)
        .orderBy('createdAt', 'desc')
        .limit(1),
    queryKey: ['chat', 'context', context, contextRefId],
  }),

  /**
   * Find all chats for a user
   */
  findByUserId: (userId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .selectAll()
        .where('userId', '=', userId)
        .where('deletedAt', 'is', null)
        .orderBy('updatedAt', 'desc'),
    queryKey: ['chat', 'userId', userId],
  }),

  /**
   * Find all non-deleted chats with the timestamp of their last message
   * (`lastMessageAt`, null for an empty chat) for the sidebar list. Not filtered
   * by userId: locally-created chats carry a null userId until backend sync, so
   * the sidebar would otherwise miss a just-created conversation (mirrors the
   * "don't filter by userId" rationale in getOrCreateChat). Final ordering and
   * date-grouping happen in the panel off `lastMessageAt ?? createdAt`.
   */
  findAllActive: (): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .leftJoin('ChatMessage', 'ChatMessage.chatId', 'Chat.id')
        .select([
          'Chat.id',
          'Chat.userId',
          'Chat.niceId',
          'Chat.context',
          'Chat.contextRefId',
          'Chat.name',
          'Chat.createdAt',
          'Chat.updatedAt',
          'Chat.deletedAt',
          // ISO-8601 strings sort lexicographically, so max() is the latest.
          db.fn.max('ChatMessage.createdAt').as('lastMessageAt'),
        ])
        .where('Chat.deletedAt', 'is', null)
        .groupBy([
          'Chat.id',
          'Chat.userId',
          'Chat.niceId',
          'Chat.context',
          'Chat.contextRefId',
          'Chat.name',
          'Chat.createdAt',
          'Chat.updatedAt',
          'Chat.deletedAt',
        ]),
    queryKey: ['chat', 'all', 'active', 'withLastMessage'],
  }),

  /**
   * Find the most recently created active chat for a user
   */
  findActiveChat: (userId: string): QueryBuilderSpec<unknown> => ({
    createQuery: (db: LocalDatabase) =>
      db
        .selectFrom('Chat')
        .selectAll()
        .where('userId', '=', userId)
        .where('deletedAt', 'is', null)
        .orderBy('createdAt', 'desc')
        .limit(1),
    queryKey: ['chat', 'active', 'userId', userId],
  }),
};
